
import { defineStore } from 'pinia';
 import { IAppointment, IPublicAppointment, IAppointmentHistoryEntry } from '@/types/appointment.types';
 import { Pagination } from '@/types/common.types';
 import api from '@/services/api.service';
 import { getApiErrorMessage } from '@/utils/apiError';
 
interface AppointmentState {
   appointments: IAppointment[];
   pagination: Pagination | null;
   isLoading: boolean;
   error: string | null;
 }
 
interface FetchAppointmentsParams {
   page?: number;
   limit?: number;
   search?: string;
   startDate?: string;
   endDate?: string;
   view?: string;
   status?: string;
 }
 
export const useAppointmentStore = defineStore('appointment', {
   state: (): AppointmentState => ({
     appointments: [],
     pagination: null,
     isLoading: false,
     error: null,
   }),
   actions: {
     async fetchAppointments(params: FetchAppointmentsParams = {}) {
       this.isLoading = true;
       this.error = null;
       try {
         const response = await api.get('/appointments', { params });
         this.appointments = response.data.data.appointments || [];
         this.pagination = response.data.data.pagination || null;
       } catch (error: any) {
         this.error = getApiErrorMessage(error, 'Failed to fetch appointments');
       } finally {
         this.isLoading = false;
       }
     },
     async createAppointment(appointmentData: any): Promise<IAppointment> {
       this.isLoading = true;
       this.error = null;
       try {
         const response = await api.post('/appointments', appointmentData);
         // Assuming the API returns the created appointment in response.data.data
         return response.data.data;
       } catch (error: any) {
         this.error = getApiErrorMessage(error, 'Failed to create appointment');
         throw error;
       } finally {
         this.isLoading = false;
       }
     },
 
    async fetchAppointmentById(id: string): Promise<IAppointment> {
      const response = await api.get(`/appointments/${id}`);
      return response.data.data;
    },

    async fetchAppointmentHistory(id: string): Promise<IAppointmentHistoryEntry[]> {
      const response = await api.get(`/appointments/${id}/history`);
      return response.data.data || [];
    },

    async cancelAppointment(id: string, reason: string): Promise<IAppointment> {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.post(`/appointments/${id}/cancel`, { reason });
        const updated = response.data.data;
        const index = this.appointments.findIndex(a => a.id === id);
        if (index !== -1) {
          this.appointments[index] = updated;
        }
        return updated;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to cancel appointment');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async updateAppointmentDetails(id: string, appointmentData: any) {
       this.isLoading = true;
       this.error = null;
       try {
         const response = await api.put(`/appointments/${id}/details`, appointmentData);
         // Optionally update the local state with the returned appointment
         const index = this.appointments.findIndex(a => a.id === id);
         if (index !== -1) {
           this.appointments[index] = response.data.data;
         }
         return response.data.data;
       } catch (error: any) {
         this.error = getApiErrorMessage(error, 'Failed to update appointment details');
         throw error;
       } finally {
         this.isLoading = false;
       }
     },

       // Public create appointment action
    async createPublicAppointment(appointmentData: any): Promise<IAppointment> {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.post('/appointments/public', appointmentData);
        return response.data.data;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to create public appointment');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    async createAcademyEnquiry(payload: { clientId: string; notes?: string }): Promise<IAppointment> {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await api.post('/appointments/public/academy-enquiry', payload);
        return response.data.data;
      } catch (error: any) {
        this.error = getApiErrorMessage(error, 'Failed to submit academy enquiry');
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
   },
 });
